import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Check, ChevronsUpDown, LoaderCircle, Plus, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { CREATE_PHOTO_TAG, PHOTO_TAGS } from '../api'
import { describePhotosError, photosErrorKey } from '../lib/errors'
import { sortTagsForPicker, tagColorClass, tagNameTaken } from '../lib/tags'
import type { PhotoTag } from '../types'

interface Props {
  projectId: string
  value: string[]
  onChange: (ids: string[]) => void
  allowCreate: boolean
}

const TagPicker: React.FC<Props> = ({ projectId, value, onChange, allowCreate }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const { data } = useQuery<{ photoTags: PhotoTag[] }>(PHOTO_TAGS, { variables: { projectId }, skip: !projectId })
  const [createTag, { loading: creating }] = useMutation(CREATE_PHOTO_TAG, {
    context: { handlesErrors: true },
    refetchQueries: ['photoTags'],
  })

  const tags = sortTagsForPicker(data?.photoTags ?? [])
  const q = query.trim()
  const filtered = q ? tags.filter((x) => x.name.toLowerCase().indexOf(q.toLowerCase()) >= 0) : tags
  const exact = tagNameTaken(q, tags)
  const selected = value.map((id) => tags.find((x) => x.id === id)).filter((x): x is PhotoTag => !!x)

  const toggle = (id: string) => onChange(value.indexOf(id) >= 0 ? value.filter((x) => x !== id) : value.concat(id))

  const create = async () => {
    try {
      const res = await createTag({ variables: { projectId, name: q, color: 'gray' } })
      onChange(value.concat(res.data.createPhotoTag.id))
      setQuery('')
    } catch (e: any) {
      toastr.error(t('Common_error'), describePhotosError(photosErrorKey(e), t))
    }
  }

  return (
    <div className="space-y-2">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button type="button" variant="outline" className="h-9 w-full justify-between text-sm font-normal">
            <span className="text-muted-foreground">{t('Photos_tags_select')}</span>
            <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[240px] p-2" align="start">
          <Input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('Photos_tags_search')} className="mb-2 h-8 text-sm" />
          <div className="max-h-56 space-y-0.5 overflow-y-auto">
            {filtered.map((tag) => (
              <button
                key={tag.id}
                type="button"
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => toggle(tag.id)}
              >
                <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', tagColorClass(tag.color))} />
                <span className="min-w-0 flex-1 truncate">{tag.name}</span>
                <Check className={cn('h-4 w-4', value.indexOf(tag.id) >= 0 ? 'opacity-100' : 'opacity-0')} />
              </button>
            ))}
            {allowCreate && q && !exact && (
              <button
                type="button"
                disabled={creating}
                className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent disabled:opacity-50"
                onClick={create}
              >
                {creating ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                {t('Photos_tags_create', { name: q })}
              </button>
            )}
            {!filtered.length && !(allowCreate && q && !exact) && <p className="px-2 py-1.5 text-sm text-muted-foreground">{t('Photos_tags_none')}</p>}
          </div>
        </PopoverContent>
      </Popover>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((tag) => (
            <Badge key={tag.id} variant="secondary" className="cursor-pointer gap-1 whitespace-nowrap" onClick={() => toggle(tag.id)}>
              <span className={cn('h-2 w-2 rounded-full', tagColorClass(tag.color))} />
              {tag.name}
              <X className="h-3 w-3" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}

export default TagPicker
